import { apiClient } from "@/config/api.config";
import axios from "axios";
import { toast } from "react-hot-toast";

const apiService = {
  getProductByTitle: async (uniqueTitle) => {
    if (!uniqueTitle || typeof uniqueTitle !== "string") {
      throw new Error("Invalid or missing uniqueTitle");
    }
    try {
      const response = await apiClient.get(
        `/products/user/product/${uniqueTitle}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch product"
      );
    }
  },

  getAllWallets: async (queryParams = {}) => {
    try {
      const response = await apiClient.get("/admin/wallets", {
        params: queryParams,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch wallets"
      );
    }
  },

  updateProduct: async (uniqueTitle, updateData) => {
    try {
      const response = await apiClient.put(
        `/products/admin/product/${uniqueTitle}`,
        updateData
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update product"
      );
    }
  },

  addProductNews: async (productId, newsData) => {
    try {
      const response = await apiClient.post(
        `/products/admin/${productId}/news`,
        newsData
      );
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          success: false,
          message: "Failed to add news",
        }
      );
    }
  },

  updateProductNews: async (productId, newsId, newsData) => {
    try {
      const response = await apiClient.put(
        `/products/update/${productId}/news/${newsId}`,
        newsData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteProductNews: async (productId, newsId) => {
    try {
      const response = await apiClient.post(
        `/products/delete/${productId}/news/${newsId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Watchlist
  createWatchlist: async (data) => {
    if (!data?.name || typeof data.isPublic !== "boolean") {
      throw new Error("Invalid watchlist data");
    }
    try {
      const response = await apiClient.post("/products/watchlist", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to create watchlist"
      );
    }
  },

  addProductToWatchlist: async (data) => {
    try {
      const payload = {
        watchlistId: data.watchlistId,
        productId: data.productId,
        ...(data.notes && { notes: data.notes }),
        ...(data.alertSettings && { alertSettings: data.alertSettings }),
      };
      const response = await apiClient.post(
        "/products/watchlist/addproduct",
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to add product to watchlist"
      );
    }
  },

  getUserWatchlists: async () => {
    try {
      const response = await apiClient.get("/products/watchlist/user");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch watchlists"
      );
    }
  },

  updateWatchlist: async (watchlistId, data) => {
    try {
      const response = await apiClient.put(
        `/products/watchlists/${watchlistId}`,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update watchlist"
      );
    }
  },

  updateWatchlistAlerts: async (data) => {
    try {
      const response = await apiClient.put("/products/watchlist/alerts", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update alerts"
      );
    }
  },

  deleteProductFromWatchlist: async (watchlistId, productId) => {
    try {
      const response = await apiClient.delete(
        `/products/watchlist/${watchlistId}/product/${productId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to remove product"
      );
    }
  },

  deleteWatchlist: async (watchlistId) => {
    try {
      const response = await apiClient.delete(
        `/products/watchlist/${watchlistId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to delete watchlist"
      );
    }
  },

  // Screener
  // getScreenerData: async (sortByRecent = false) => {
  //   try {
  //     const response = await apiClient.post("/products/screener/execute", {
  //       sortByRecent: sortByRecent,
  //     });
  //     return response.data;
  //   } catch (error) {
  //     throw new Error(
  //       error.response?.data?.message || "Failed to fetch screener data"
  //     );
  //   }
  // },
  getScreenerData: async (options = {}) => {
    try {
      const {
        sortByName,
        sortByRecent = false,
        page = 1,
        limit = 20,
        filterCriteria = null
      } = options;

      const response = await apiClient.post("/products/screener/execute", {
        sortByName,
        sortByRecent,
        page,
        limit,
        filterCriteria
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch screener data"
      );
    }
  },
  saveScreener: async (data) => {
    try {
      const response = await apiClient.post("/products/screener/save", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to save screener"
      );
    }
  },

  getUserScreeners: async () => {
    try {
      const response = await apiClient.get("/products/screener");
      return response.data.data || [];
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch screeners"
      );
    }
  },

  updateScreener: async (screenerId, data) => {
    try {
      const response = await apiClient.put(
        `/products/screener/${screenerId}`,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update screener"
      );
    }
  },

  getallfiters: async () => {
    try {
      const response = await apiClient.get("/admin/all");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch filter options"
      );
    }
  },

  deleteScreener: async (screenerId) => {
    try {
      const response = await apiClient.delete(
        `/products/screener/${screenerId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to delete screener"
      );
    }
  },

  // Portfolio
  getUserHoldings: async () => {
    try {
      const response = await apiClient.get("/portfolio/user/portfolios");
      return response.data.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch user holdings"
      );
    }
  },

  getPortfolioById: async (portfolioId) => {
    try {
      const response = await apiClient.get(`/portfolio/${portfolioId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch portfolio"
      );
    }
  },

  getInvestmentRecommendations: async (portfolioId) => {
    try {
      const response = await apiClient.get(
        `/portfolio/${portfolioId}/recommendations`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
        "Failed to fetch investment recommendations"
      );
    }
  },

  addHolding: async (portfolioId, holdingData) => {
    try {
      const response = await apiClient.post(
        `/portfolio/${portfolioId}/holdings`,
        holdingData
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to add holding");
    }
  },

  getPortfolioSolution: async (portfolioId, solutionData) => {
    try {
      const response = await apiClient.get(
        `/portfolio/${portfolioId}/solution`,
        solutionData
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to get portfolio solution"
      );
    }
  },

  updatePortfolio: async (portfolioId, data) => {
    try {
      const response = await apiClient.put(
        `/portfolio/${portfolioId}/update`,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update portfolio"
      );
    }
  },

  deletePortfolio: async (portfolioId) => {
    const toastId = toast.loading("Deleting portfolio...");
    try {
      const response = await apiClient.delete(
        `/portfolio/${portfolioId}/delete`
      );
      toast.success(response.data.message || "Portfolio deleted", {
        id: toastId,
      });
      return response.data;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete portfolio",
        { id: toastId }
      );
      throw error;
    }
  },

  createPortfolio: async (data) => {
    try {
      const response = await apiClient.post("/portfolio/create", data);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.error || error.message || "Something went wrong";
      throw new Error(message);
    }
  },

  //product for iuser
  // New methods for availability
  getProductAvailabilityById: async (productId) => {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    try {
      const response = await apiClient.get(
        `/products/user/product/${productId}/availability`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch product availability"
      );
    }
  },

  getProductAvailabilityByTitle: async (uniqueTitle) => {
    if (!uniqueTitle) {
      throw new Error("Product unique title is required");
    }
    try {
      // First get the product ID by title
      const productResponse = await apiService.getProductByTitle(uniqueTitle);
      if (!productResponse.success || !productResponse.data?._id) {
        throw new Error("Product not found");
      }

      // Then get availability by ID
      const availabilityResponse = await apiService.getProductAvailabilityById(
        productResponse.data._id
      );
      return availabilityResponse;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch product availability"
      );
    }
  },
  createSellRequest: async (data) => {
    try {
      const response = await apiClient.post("/transactions/sell-request", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to create sell request"
      );
    }
  },
  getSellRequests: async (params = {}) => {
    try {
      const response = await apiClient.get("/transactions/sell-requests", {
        params,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch sell requests"
      );
    }
  },

  // Cancel sell request
  cancelSellRequest: async (requestId) => {
    try {
      const response = await apiClient.delete(`/transactions/${requestId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || "Failed to cancel sell request"
      );
    }
  },
  getSellRequestDetails: async (requestId) => {

    try {
      const response = await apiClient.get(`/transactions/${requestId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch sell request details"
      );
    }
  },

  getAsperoInvestments: async () => {
    try {
      const response = await apiClient.get("/aspero/my-investments");
      return response.data?.data || [];
    } catch (error) {
      console.error("Failed to fetch aspero investments:", error);
      return []; // Return empty array softly
    }
  },
};

export default apiService;
